import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowRight, Loader2 } from "lucide-react";
import { api, formatApiError, setStoredToken } from "@/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

/**
 * OnboardPage — deliberately requires an explicit "Sign me in" tap.
 *
 * Prior versions auto-fired POST /auth/onboard-scan on mount. That broke in
 * subtle ways:
 *   • Link previewers and URL scanners (WhatsApp, iMessage, corporate AV,
 *     Google Safe Browsing) followed the URL and consumed the single-use
 *     token before the user ever tapped it.
 *   • WhatsApp/Instagram in-app WebViews on iOS silently drop the
 *     SameSite=None auth cookie. Even with the Bearer-token fallback in
 *     localStorage, the token gets written into the WebView's sandbox,
 *     which does NOT share storage with Safari or the installed PWA — so
 *     the token is burned in a place the user will never sign in from.
 *   • React 18 StrictMode's mount → unmount → remount fired the POST twice.
 *
 * The button-driven flow eliminates all four:
 *   1. GET /auth/onboard-preview/:token is idempotent — safe against every
 *      bot/scanner and against StrictMode double-invoke.
 *   2. POST /auth/onboard-scan only runs when the user physically taps the
 *      button. If they realise they're stuck inside WhatsApp, they can
 *      "Open in Chrome/Safari" first and re-tap — the token is still
 *      unused until they commit.
 */
export default function OnboardPage() {
    const { token } = useParams();
    const navigate = useNavigate();
    const { refresh } = useAuth();

    const [preview, setPreview] = useState(null); // { user_name, role, workspace_name }
    const [previewErr, setPreviewErr] = useState("");
    const [loading, setLoading] = useState(true);
    const [signingIn, setSigningIn] = useState(false);
    const [signInErr, setSignInErr] = useState("");

    // Idempotent preview fetch — safe if fired twice.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get(`/auth/onboard-preview/${encodeURIComponent(token)}`);
                if (cancelled) return;
                setPreview(data);
            } catch (e) {
                if (cancelled) return;
                setPreviewErr(formatApiError(e));
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [token]);

    const signIn = async () => {
        if (signingIn) return;
        setSigningIn(true);
        setSignInErr("");
        try {
            const { data } = await api.post("/auth/onboard-scan", { token });
            // Persist the JWT client-side so in-app browsers that drop the
            // SameSite=None cookie still stay signed in via Bearer header.
            if (data?.access_token) setStoredToken(data.access_token);
            await refresh();
            toast.success(`Welcome, ${data.name.split(" ")[0]}!`);
            navigate(data.role === "manager" ? "/manager" : "/operative", { replace: true });
        } catch (e) {
            setSignInErr(formatApiError(e));
            setSigningIn(false);
        }
    };

    // --- Loading state ---
    if (loading) {
        return (
            <Shell>
                <Loader2 className="animate-spin mx-auto text-[var(--pine)]" size={28} data-testid="onboard-loading" />
                <p className="text-sm text-[var(--text-muted)] mt-4">Checking your sign-in link…</p>
            </Shell>
        );
    }

    // --- Preview failed (invalid / expired / used) ---
    if (previewErr) {
        return (
            <Shell>
                <div className="font-display text-2xl font-semibold text-red-700" data-testid="onboard-preview-error">
                    We couldn't use this link
                </div>
                <p className="text-sm text-[var(--text-secondary)] mt-3">{previewErr}</p>
                <button
                    data-testid="onboard-back-to-login"
                    onClick={() => navigate("/login", { replace: true })}
                    className="btn-primary mt-8">
                    Go to sign in
                </button>
            </Shell>
        );
    }

    // --- Ready to sign in ---
    const firstName = (preview?.user_name || "").split(" ")[0];
    return (
        <Shell>
            <div className="text-xs uppercase tracking-[0.24em] text-[var(--terracotta)] font-semibold" data-testid="onboard-workspace">
                {preview?.workspace_name || "SCS"}
            </div>
            <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-2" data-testid="onboard-greeting">
                Welcome{firstName ? `, ${firstName}` : ""}
            </h1>
            <p className="text-sm text-[var(--text-secondary)] mt-3">
                Tap the button below to sign in — you'll stay signed in on this device from now on.
            </p>

            <button
                data-testid="onboard-signin-btn"
                onClick={signIn}
                disabled={signingIn}
                className="btn-primary mt-8 w-full inline-flex items-center justify-center gap-2 text-base py-3">
                {signingIn ? (
                    <>
                        <Loader2 size={16} className="animate-spin" /> Signing you in…
                    </>
                ) : (
                    <>
                        Sign me in <ArrowRight size={16} />
                    </>
                )}
            </button>

            {signInErr && (
                <div data-testid="onboard-signin-error"
                     className="mt-4 text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2 text-left">
                    {signInErr}
                </div>
            )}

            <div className="mt-6 text-[11px] text-[var(--text-muted)] leading-relaxed" data-testid="onboard-webview-hint">
                Opened this from WhatsApp? For the smoothest experience, tap the ⋮ / share menu and choose <span className="font-medium">Open in Chrome</span> (Android) or <span className="font-medium">Open in Safari</span> (iPhone) before tapping "Sign me in".
            </div>
        </Shell>
    );
}

function Shell({ children }) {
    return (
        <div className="min-h-screen flex items-center justify-center px-6 py-10 bg-[var(--bg)]">
            <div className="w-full max-w-sm text-center animate-in-up">
                <img src="/scs-icon-512.png" alt="SCS" className="h-14 w-14 rounded-2xl object-cover mx-auto mb-8" />
                {children}
            </div>
        </div>
    );
}
